import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  adminRole: 'owner' | 'staff' | 'none';
  staffRoles?: string[];
}

interface DuplicateForm {
  name: string;
  startDate: string;
  includeTeams: boolean;
  includeSideAction: boolean;
}

const ManageLeagues: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Duplicate modal state
  const [duplicateSource, setDuplicateSource] = useState<League | null>(null);
  const [dupForm, setDupForm] = useState<DuplicateForm>({ name: '', startDate: '', includeTeams: true, includeSideAction: true });
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [dupError, setDupError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiUrl}/leagues/admin`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setLeagues(data);
        }
      } catch (error) {
        console.error('Failed to fetch leagues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();
  }, []);

  const getLeagueStatusRaw = (league: League) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const startDate = new Date(league.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(league.endDate);
    endDate.setHours(23, 59, 59, 999);

    if (!league.isActive) return 'Inactive';
    if (now < startDate) return 'Upcoming';
    if (now > endDate) return 'Completed';
    return 'Active';
  };

  const getLeagueStatus = (league: League) => {
    const status = getLeagueStatusRaw(league);
    switch (status) {
      case 'Inactive': return t('admin.manageLeagues.status.inactive');
      case 'Upcoming': return t('admin.manageLeagues.status.upcoming');
      case 'Completed': return t('admin.manageLeagues.status.completed');
      case 'Active': return t('admin.manageLeagues.status.active');
      default: return t('admin.manageLeagues.status.active');
    }
  };

  const filteredLeagues = leagues.filter(league => {
    const matchesSearch = league.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      league.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
      league.city.toLowerCase().includes(searchTerm.toLowerCase());

    const leagueStatusRaw = getLeagueStatusRaw(league);
    const matchesStatus = statusFilter === 'All' || leagueStatusRaw === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const openDuplicate = (e: React.MouseEvent, league: League) => {
    e.stopPropagation();
    setDuplicateSource(league);
    setDupForm({
      name: `${league.name} (Copy)`,
      startDate: '',
      includeTeams: true,
      includeSideAction: true,
    });
    setDupError(null);
  };

  const closeDuplicate = () => {
    setDuplicateSource(null);
    setDupError(null);
  };

  const handleDuplicate = async () => {
    if (!duplicateSource) return;
    if (!dupForm.name.trim()) { setDupError('League name is required.'); return; }
    if (!dupForm.startDate) { setDupError('Start date is required.'); return; }

    setIsDuplicating(true);
    setDupError(null);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/leagues/${duplicateSource.id}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: dupForm.name.trim(),
          startDate: dupForm.startDate,
          includeTeams: dupForm.includeTeams,
          includeSideAction: dupForm.includeSideAction,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setDupError(data.error || 'Failed to duplicate league.'); return; }
      // Refresh the league list and close modal
      setLeagues(prev => [...prev]);
      closeDuplicate();
      // Re-fetch leagues
      const listRes = await fetch(`${apiUrl}/leagues/admin`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (listRes.ok) setLeagues(await listRes.json());
    } catch {
      setDupError('Network error. Please try again.');
    } finally {
      setIsDuplicating(false);
    }
  };



  const getStatusColor = (statusRaw: string) => {
    switch (statusRaw) {
      case 'Active': return 'bg-green-500/20 text-green-200 border-green-400';
      case 'Upcoming': return 'bg-blue-500/20 text-blue-200 border-blue-400';
      case 'Completed': return 'bg-gray-500/20 text-gray-200 border-gray-400';
      case 'Inactive': return 'bg-red-500/20 text-red-200 border-red-400';
      default: return 'bg-purple-500/20 text-purple-200 border-purple-400';
    }
  };

  if (!user || user.type !== 'admin') {
    navigate('/admin/dashboard');
    return null;
  }

  return (
    <React.Fragment>
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">{t('admin.manageLeagues.title')}</h1>
              <p className="text-purple-200 mt-1">
                {t('admin.manageLeagues.subtitle')}
              </p>
            </div>
            <div className="flex space-x-4">

              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200"
              >
                {t('admin.manageLeagues.backToDashboard')}
              </button>
              <button
                onClick={() => navigate('/admin/leagues/create')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200"
              >
                {t('admin.manageLeagues.createNewLeague')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t('admin.manageLeagues.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="All" className="bg-gray-800">{t('admin.manageLeagues.allStatus')}</option>
                <option value="Active" className="bg-gray-800">{t('admin.manageLeagues.status.active')}</option>
                <option value="Upcoming" className="bg-gray-800">{t('admin.manageLeagues.status.upcoming')}</option>
                <option value="Completed" className="bg-gray-800">{t('admin.manageLeagues.status.completed')}</option>
                <option value="Inactive" className="bg-gray-800">{t('admin.manageLeagues.status.inactive')}</option>
              </select>
            </div>
            <div className="text-purple-200 text-sm self-center">
              {t('admin.manageLeagues.leaguesFound', { count: filteredLeagues.length })}
            </div>
          </div>
        </div>

        {/* Leagues Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-purple-200 text-lg">{t('admin.manageLeagues.loading')}</p>
          </div>
        ) : filteredLeagues.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎳</div>
            <h3 className="text-xl font-bold text-white mb-2">
              {searchTerm ? t('admin.manageLeagues.noLeaguesFound') : t('admin.manageLeagues.noLeaguesCreated')}
            </h3>
            <p className="text-purple-200 mb-6">
              {searchTerm
                ? t('admin.manageLeagues.tryAdjusting')
                : t('admin.manageLeagues.createFirstLeague')
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/admin/leagues/create')}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200"
              >
                {t('admin.manageLeagues.createYourFirstLeague')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLeagues.map((league) => (
              <div
                key={league.id}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 cursor-pointer group"
                onClick={() => {
                  navigate(`/admin/leagues/${league.id}/manage`, {
                    state: {
                      staffRoles: league.staffRoles || []
                    }
                  });
                }}
              >
                {/* League Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-purple-200 transition">
                      {league.name}
                    </h3>
                    <p className="text-purple-300 text-sm">
                      {league.venue}, {league.city}
                    </p>
                  </div>
                  
                  <div className={`px-2 py-1 rounded text-xs border ${getStatusColor(getLeagueStatusRaw(league))}`}>
                    {getLeagueStatus(league)}
                  </div>

                  {league.adminRole === 'owner' && (
                    <div className="px-2 py-1 ml-1 text-blue-200 bg-blue-700/50 rounded border border-blue-500/80 text-xs">Owner</div>
                  )}
                  {league.adminRole === 'staff' && (
                    <div className="px-2 py-1 ml-1 text-yellow-200 bg-yellow-700/50 rounded border border-yellow-500/80 text-xs">Staff</div>
                  )}

                </div>

                {/* League Details */}
                <div className="space-y-2 text-sm text-purple-200">
                  <div className="flex justify-between">
                    <span>{t('admin.manageLeagues.format')}</span>
                    <span className="text-white capitalize">{league.teamFormat}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('admin.manageLeagues.startDate')}</span>
                    <span className="text-white">{formatDate(league.startDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('admin.manageLeagues.endDate')}</span>
                    <span className="text-white">{formatDate(league.endDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('admin.manageLeagues.created')}</span>
                    <span className="text-white">{formatDate(league.createdAt)}</span>
                  </div>
                </div>

                {/* League Description */}
                {league.description && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-purple-200 text-sm line-clamp-2">
                      {league.description}
                    </p>
                  </div>
                )}

                {/* Action Hint */}
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
                  <p className="text-purple-300 text-xs group-hover:text-purple-200 transition">
                    {t('admin.manageLeagues.clickToManage')}
                  </p>
                  {league.adminRole === 'owner' && (
                    <button
                      onClick={(e) => openDuplicate(e, league)}
                      className="px-3 py-1 text-xs font-semibold rounded-lg bg-indigo-600/70 hover:bg-indigo-500 border border-indigo-400/50 text-indigo-100 transition-colors whitespace-nowrap"
                    >
                      Duplicate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* ── Duplicate League Modal ─────────────────────────────────────────── */}
    {duplicateSource && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-gray-900 via-purple-950 to-indigo-950 border border-white/20 rounded-2xl shadow-2xl w-full max-w-md">
          {/* Modal header */}
          <div className="flex items-start justify-between p-6 border-b border-white/10">
            <div>
              <h2 className="text-xl font-bold text-white">Duplicate League</h2>
              <p className="text-purple-300 text-sm mt-1">
                Copy this setup, the teams in this event and their current calc averages for the next league?
              </p>
            </div>
            <button
              onClick={closeDuplicate}
              className="text-gray-400 hover:text-white text-2xl leading-none ml-4 mt-0.5"
            >×</button>
          </div>

          {/* Source badge */}
          <div className="px-6 pt-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
              <span className="text-purple-300 text-xs">Copying from:</span>
              <span className="text-white text-sm font-semibold truncate">{duplicateSource.name}</span>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            {/* League Name */}
            <div>
              <label className="block text-purple-200 text-sm font-medium mb-1">New League Name</label>
              <input
                type="text"
                value={dupForm.name}
                onChange={e => setDupForm(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                placeholder="League name…"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-purple-200 text-sm font-medium mb-1">Start Date</label>
              <input
                type="date"
                value={dupForm.startDate}
                onChange={e => setDupForm(f => ({ ...f, startDate: e.target.value }))}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
              <p className="text-purple-400 text-xs mt-1">End date is calculated automatically from your scheduled squads.</p>
            </div>

            {/* Checkboxes */}
            <div className="space-y-2">
              <p className="text-purple-200 text-sm font-medium">Also copy:</p>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={dupForm.includeTeams}
                  onChange={e => setDupForm(f => ({ ...f, includeTeams: e.target.checked }))}
                  className="w-4 h-4 rounded border-white/30 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-white text-sm font-medium">Teams</span>
                  <p className="text-purple-400 text-xs">All teams, bowlers, and their calculated averages as set averages</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={dupForm.includeSideAction}
                  onChange={e => setDupForm(f => ({ ...f, includeSideAction: e.target.checked }))}
                  className="w-4 h-4 rounded border-white/30 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-white text-sm font-medium">Side Action</span>
                  <p className="text-purple-400 text-xs">All side events and their configurations (brackets, survivors, etc.)</p>
                </div>
              </label>
            </div>

            {/* Error */}
            {dupError && (
              <div className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 text-sm">
                {dupError}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex gap-3">
            <button
              onClick={closeDuplicate}
              disabled={isDuplicating}
              className="flex-1 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDuplicate}
              disabled={isDuplicating}
              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition text-sm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isDuplicating ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Duplicating…
                </>
              ) : 'Duplicate League'}
            </button>
          </div>
        </div>
      </div>
    )}
    </React.Fragment>
  );
};

export default ManageLeagues;